import { BusinessKeywordsMapping } from 'api/client/mapping/business-keywords-mapping';
import { IQuestionDetailDTO, QuestionsClient } from 'api/swagger/ApiClient';
import { autoinject } from 'aurelia-framework';
import { Redirect, RouteConfig, Router } from 'aurelia-router';
import { watch } from 'aurelia-watch-decorator';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { log } from 'framework/script_utilities/dev-decorators';
import { QuestionDetailModel } from 'model/forms/question-detail-data';
import { CountryWrapper } from 'model/support-types';
import { LookupsService } from 'services/lookups-service';
import { requireServices } from 'support/activation';
import { HOME_ROUTE } from 'support/settings';

@autoinject
export class AdminQuestionsScreen {
  route: RouteConfig = null;
  filterText: string = null;
  includeArchived = false;
  questions: QuestionDetailModel[] = [];
  loaded = false;

  constructor(private lookups: LookupsService, private api: QuestionsClient, private router: Router, private dialogHelper: DialogHelper, private businessKeywordsMapping: BusinessKeywordsMapping) {
  }

  async canActivate() {
    await requireServices(this.lookups);
  }

  @log('debug')
  async activate(params, route: RouteConfig) {
    this.route = route;
  }

  bind() {
    this.initialize();
  }

  private async initialize() {
    this.bindDetail(await this.api.getAll());
    this.loaded = true;
  }

  async canDeactivate() {
    if (this.questions.some(x => x.isQuestionEditing) && !await this.dialogHelper.confirm({ dialogTitle: 'Leave without saving?', message: 'You haven\'t saved the form yet. If you proceed, your changes will be lost!', okTitle: 'Leave without saving', cancelTitle: 'Stay here!' }))
      return new Redirect((this.router.history as any).previousLocation, { trigger: false, replace: false });
  }

  createNewQuestion() {
    if (!this.questions.some(x => x.data.id == null)) {
      this.questions.unshift({
        data: {
          name: null,
          isActive: true,
          isDefaultChecked: false,
          isMandatory: false
        } as IQuestionDetailDTO,
        isVisible: true,
        isQuestionEditing: true,
        isExpanded: true,
        isGlobal: true,
        countriesWrapper: [],
        questionDataChanged: (item: QuestionDetailModel) => {
          item.isVisible = this.getIsVisible(this.getFilterText(), item);
        }
      } as QuestionDetailModel)
    }
  }

  cancelCreateNewQuestion() {
    const newQuestionIndex = this.questions.findIndex(x => x.data.id == null);
    if (newQuestionIndex > -1)
      this.questions.splice(newQuestionIndex, 1);
  }

  @log('debug')
  private bindDetail(dtos: IQuestionDetailDTO[]) {
    this.questions = dtos.map(dto => ({
      data: dto,
      isVisible: true,
      isQuestionEditing: false,
      isExpanded: false,
      isGlobal: dto.isGlobal,
      countriesWrapper: dto.countries.map(x => ({ country: this.businessKeywordsMapping.mapToCountry(x) } as CountryWrapper)),
      questionDataChanged: (item: QuestionDetailModel) => {
        item.isVisible = this.getIsVisible(this.getFilterText(), item);
      }
    } as QuestionDetailModel));
    this.filterMutated();
  }

  @watch((x: AdminQuestionsScreen) => x.filterText)
  @watch((x: AdminQuestionsScreen) => x.includeArchived)
  filterMutated() {
    const filterText = this.getFilterText();
    const visibleQuestions = this.questions
      .filter(x => this.getIsVisible(filterText, x))
      .map(x => x.data.id);
    this.questions.forEach(question => {
      question.isVisible = visibleQuestions.includes(question.data.id);
    })
  }

  private getFilterText() {
    return this.filterText?.trim().toLowerCase();
  }

  public getIsVisible(filterText: string, item: QuestionDetailModel): boolean {
    return (filterText == null || item.data.name.toLowerCase().includes(filterText) || item.countriesWrapper.some(x => x.country.name.toLowerCase().includes(filterText)))
      && (this.includeArchived || item.data.isActive);
  }

  get homeRoute() {
    return HOME_ROUTE;
  }

  get title() {
    return 'Questions';
  }

  archiveOptions = [
    { value: false, name: 'Hide archived items' },
    { value: true, name: 'Show archived items' }
  ];
}
